import type {
  BulkCreateTagsRequest,
  ReplaceTagsRequest,
  SuggestionFilters,
  TimeEntryTagFilters,
} from '@/lib/api/time-entry-tags.api'
import { getMutationErrorMessage } from '@/lib/api/mutation-feedback'
import { timeEntryTagApi } from '@/lib/api/time-entry-tags.api'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
  CreateTimeEntryTag,
  TimeEntryTag,
  UpdateTimeEntryTag,
} from '@scrambl/shared'

// Query keys
export const timeEntryTagKeys = {
  all: ['timeEntryTags'] as const,
  lists: () => [...timeEntryTagKeys.all, 'list'] as const,
  list: (filters: TimeEntryTagFilters) =>
    [...timeEntryTagKeys.lists(), filters] as const,
  details: () => [...timeEntryTagKeys.all, 'detail'] as const,
  detail: (id: string) => [...timeEntryTagKeys.details(), id] as const,
  byTimeEntry: (timeEntryId: string) =>
    [...timeEntryTagKeys.all, 'timeEntry', timeEntryId] as const,
  activeByTimeEntry: (timeEntryId: string) =>
    [...timeEntryTagKeys.byTimeEntry(timeEntryId), 'active'] as const,
  mySuggestions: (filters: SuggestionFilters) =>
    [...timeEntryTagKeys.all, 'mySuggestions', filters] as const,
}

/**
 * Hook to get all time entry tags with filters
 */
export function useTimeEntryTags(filters: TimeEntryTagFilters = {}) {
  return useQuery({
    queryKey: timeEntryTagKeys.list(filters),
    queryFn: () => timeEntryTagApi.getTimeEntryTags(filters),
  })
}

/**
 * Hook to get a specific time entry tag
 */
export function useTimeEntryTag(id: string) {
  return useQuery({
    queryKey: timeEntryTagKeys.detail(id),
    queryFn: () => timeEntryTagApi.getTimeEntryTag(id),
    enabled: !!id,
  })
}

/**
 * Hook to get all tags for a specific time entry
 */
export function useTagsForTimeEntry(timeEntryId: string) {
  return useQuery({
    queryKey: timeEntryTagKeys.byTimeEntry(timeEntryId),
    queryFn: () => timeEntryTagApi.getTagsForTimeEntry(timeEntryId),
    enabled: !!timeEntryId,
  })
}

/**
 * Hook to get active tags for a specific time entry
 */
export function useActiveTagsForTimeEntry(timeEntryId: string) {
  return useQuery({
    queryKey: timeEntryTagKeys.activeByTimeEntry(timeEntryId),
    queryFn: () => timeEntryTagApi.getActiveTagsForTimeEntry(timeEntryId),
    enabled: !!timeEntryId,
  })
}

/**
 * Hook to get suggestions (time entries where current user is tagged)
 */
export function useMySuggestions(filters: SuggestionFilters = {}) {
  return useQuery({
    queryKey: timeEntryTagKeys.mySuggestions(filters),
    queryFn: () => timeEntryTagApi.getMySuggestions(filters),
  })
}

/**
 * Hook to create a time entry tag
 */
export function useCreateTimeEntryTag() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateTimeEntryTag) =>
      timeEntryTagApi.createTimeEntryTag(data),
    onSuccess: (response, variables) => {
      toast.success('Tag added')

      // Invalidate relevant queries
      queryClient.invalidateQueries({
        queryKey: timeEntryTagKeys.byTimeEntry(variables.timeEntryId),
      })
      queryClient.invalidateQueries({
        queryKey: timeEntryTagKeys.activeByTimeEntry(variables.timeEntryId),
      })
      queryClient.invalidateQueries({ queryKey: timeEntryTagKeys.lists() })
    },
    onError: (error: any) => {
      toast.error(getMutationErrorMessage(error, "Couldn't add the tag."))
    },
  })
}

/**
 * Hook to create multiple time entry tags
 */
export function useBulkCreateTags() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: BulkCreateTagsRequest) =>
      timeEntryTagApi.bulkCreateTags(data),
    onSuccess: (response, variables) => {
      const createdCount = response.data.length
      toast.success(`${createdCount} tag${createdCount !== 1 ? 's' : ''} added`)

      // Invalidate relevant queries
      queryClient.invalidateQueries({
        queryKey: timeEntryTagKeys.byTimeEntry(variables.timeEntryId),
      })
      queryClient.invalidateQueries({
        queryKey: timeEntryTagKeys.activeByTimeEntry(variables.timeEntryId),
      })
      queryClient.invalidateQueries({ queryKey: timeEntryTagKeys.lists() })
    },
    onError: (error: any) => {
      toast.error(getMutationErrorMessage(error, "Couldn't add tags."))
    },
  })
}

/**
 * Hook to replace all tags for a time entry
 */
export function useReplaceTagsForTimeEntry() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      timeEntryId,
      data,
    }: {
      timeEntryId: string
      data: ReplaceTagsRequest
      suppressErrorToast?: boolean
    }) => timeEntryTagApi.replaceTagsForTimeEntry(timeEntryId, data),
    // Replacing tags is idempotent (the full set is sent each time), so retry
    // transient network failures rather than silently dropping the member tags.
    retry: 2,
    retryDelay: attempt => Math.min(500 * 2 ** attempt, 3000),
    onSuccess: (response, variables) => {
      // Don't show toast - the UI already updates optimistically
      // Just invalidate the queries to refresh the data
      queryClient.invalidateQueries({
        queryKey: timeEntryTagKeys.byTimeEntry(variables.timeEntryId),
      })
      queryClient.invalidateQueries({
        queryKey: timeEntryTagKeys.activeByTimeEntry(variables.timeEntryId),
      })
      queryClient.invalidateQueries({ queryKey: timeEntryTagKeys.lists() })
    },
    onError: (error: any, variables) => {
      // Callers that surface their own failure message (e.g. the create
      // dialog) pass suppressErrorToast to avoid a duplicate toast.
      if (variables?.suppressErrorToast) return
      toast.error(getMutationErrorMessage(error, "Couldn't update tags."))
    },
  })
}

/**
 * Hook to update a time entry tag
 */
export function useUpdateTimeEntryTag() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTimeEntryTag }) =>
      timeEntryTagApi.updateTimeEntryTag(id, data),
    onSuccess: response => {
      toast.success('Tag updated')

      // Invalidate relevant queries
      queryClient.invalidateQueries({
        queryKey: timeEntryTagKeys.detail(response.data.id),
      })
      queryClient.invalidateQueries({
        queryKey: timeEntryTagKeys.byTimeEntry(response.data.timeEntryId),
      })
      queryClient.invalidateQueries({
        queryKey: timeEntryTagKeys.activeByTimeEntry(response.data.timeEntryId),
      })
      queryClient.invalidateQueries({ queryKey: timeEntryTagKeys.lists() })
    },
    onError: (error: any) => {
      toast.error(getMutationErrorMessage(error, "Couldn't update the tag."))
    },
  })
}

/**
 * Hook to dismiss a time entry tag
 */
export function useDismissTag() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      reason,
    }: {
      id: string
      reason?: string
      // Callers that dismiss as one step of another action report it themselves.
      silent?: boolean
    }) => timeEntryTagApi.dismissTag(id, { reason }),
    onSuccess: (response, variables) => {
      if (!variables.silent) toast.success('Suggestion dismissed')

      // Invalidate relevant queries
      queryClient.invalidateQueries({
        queryKey: timeEntryTagKeys.detail(response.data.id),
      })
      queryClient.invalidateQueries({
        queryKey: timeEntryTagKeys.byTimeEntry(response.data.timeEntryId),
      })
      queryClient.invalidateQueries({
        queryKey: timeEntryTagKeys.activeByTimeEntry(response.data.timeEntryId),
      })
      queryClient.invalidateQueries({ queryKey: timeEntryTagKeys.lists() })
      // Also invalidate suggestions since dismissing affects the suggestions list
      queryClient.invalidateQueries({
        queryKey: timeEntryTagKeys.mySuggestions({}),
      })
    },
    onError: (error: any) => {
      toast.error(
        getMutationErrorMessage(error, "Couldn't dismiss the suggestion."),
      )
    },
  })
}

/**
 * Hook to remove a time entry tag
 */
export function useRemoveTag() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      timeEntryTagApi.removeTag(id, { reason }),
    onSuccess: response => {
      toast.success('Tag removed')

      // Invalidate relevant queries
      queryClient.invalidateQueries({
        queryKey: timeEntryTagKeys.detail(response.data.id),
      })
      queryClient.invalidateQueries({
        queryKey: timeEntryTagKeys.byTimeEntry(response.data.timeEntryId),
      })
      queryClient.invalidateQueries({
        queryKey: timeEntryTagKeys.activeByTimeEntry(response.data.timeEntryId),
      })
      queryClient.invalidateQueries({ queryKey: timeEntryTagKeys.lists() })
    },
    onError: (error: any) => {
      toast.error(getMutationErrorMessage(error, "Couldn't remove the tag."))
    },
  })
}

/**
 * Hook to duplicate tags between time entries
 */
export function useDuplicateTags() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      sourceTimeEntryId,
      targetTimeEntryId,
    }: {
      sourceTimeEntryId: string
      targetTimeEntryId: string
    }) =>
      timeEntryTagApi.duplicateTags({ sourceTimeEntryId, targetTimeEntryId }),
    onSuccess: (response, variables) => {
      const duplicatedCount = response.data.length
      if (duplicatedCount > 0) {
        toast.success(
          `${duplicatedCount} tag${duplicatedCount !== 1 ? 's' : ''} duplicated`,
        )
      }

      // Invalidate relevant queries for target time entry
      queryClient.invalidateQueries({
        queryKey: timeEntryTagKeys.byTimeEntry(variables.targetTimeEntryId),
      })
      queryClient.invalidateQueries({
        queryKey: timeEntryTagKeys.activeByTimeEntry(
          variables.targetTimeEntryId,
        ),
      })
      queryClient.invalidateQueries({ queryKey: timeEntryTagKeys.lists() })
    },
    onError: (error: any) => {
      toast.error(getMutationErrorMessage(error, "Couldn't duplicate tags."))
    },
  })
}

/**
 * Hook to delete a time entry tag
 */
export function useDeleteTimeEntryTag() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => timeEntryTagApi.deleteTimeEntryTag(id),
    onSuccess: (response, id) => {
      toast.success('Tag deleted')

      // Invalidate all relevant queries
      queryClient.invalidateQueries({ queryKey: timeEntryTagKeys.all })
    },
    onError: (error: any) => {
      toast.error(getMutationErrorMessage(error, "Couldn't delete the tag."))
    },
  })
}
